import {
  checkPinExisted,
  createBoard,
  createPin,
  getBoardSuggestTags,
  getLastBoards,
  getPin,
} from "@/services";
import useRequest from "@/utils/useReq";
import { InfoCircleFilled } from "@ant-design/icons";
import { useGetState, useMemoizedFn } from "ahooks";
import { notification } from "antd";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import imageUrl from "../../utils/imageUrl";
import styles from "./index.module.less";

const suggestTagsFilter = (tags) =>
  (tags ?? []).filter((tag) => tag.length <= 10).slice(0, 5);

export default function useViewModel(props) {
  const {
    pinId,
    closeWaitSeconds,
    onCloseCreator,
    closeAfterDone,
    showSuccessTip,
  } = props;

  // const [loading, setLoading] = useState(false);
  const [tip, setTip] = useState();
  const [targetAvatarUrl, setTargetAvatarUrl] = useState(props.targetAvatarUrl);
  const availBoards = useRef([]);
  const [selected, setSelected, getSelected] = useGetState([]);

  const [suggestTags, setSuggestTags] = useState(
    suggestTagsFilter(props.suggestTags ?? [])
  );

  const [, setUpdateAt] = useState(0);
  const [boards, setBoards] = useState(props.boards ?? []);
  const result = useRef({ tags: [], description: null });
  const [keyword, setKeyword] = useState();

  const [newPin, setNewPin] = useState();
  const doneResult = useRef({});
  const [autoCloseCounter, setAutoCloseCounter] = useState(
    closeWaitSeconds || 5
  );

  const {
    data: lastBoardsResult,
    run: runGetLastBoards,
    loading: lastBoardsLoading,
  } = useRequest(getLastBoards, {
    cacheKey: "lastBoards",
    manual: true,
  });

  const loading = useMemo(() => {
    return lastBoardsResult ? false : lastBoardsLoading;
  }, [lastBoardsLoading, lastBoardsResult]);

  const { data: boardSuggestTagsResult, run: runGetBoardSuggestTags } =
    useRequest(getBoardSuggestTags, {
      manual: true,
    });

  const { data: pinExistedResult, run: runCheckPinExisted } = useRequest(
    checkPinExisted,
    {
      manual: true,
    }
  );

  useEffect(() => {
    if (lastBoardsResult) {
      const boards = lastBoardsResult.boards;
      if (boards.length >= 50) {
        setTimeout(() => {
          setBoards(boards || []);
        }, 200);
      } else {
        setBoards(boards || []);
      }
    }
  }, [lastBoardsResult]);

  useEffect(() => {
    if (boardSuggestTagsResult) {
      setSuggestTags(suggestTagsFilter(boardSuggestTagsResult.tags) || []);
    }
  }, [boardSuggestTagsResult]);

  useEffect(() => {
    if (pinExistedResult && pinExistedResult.exist_pin) {
      const { exist_pin } = pinExistedResult;
      // state里面居然存dom
      setTip(
        <div className={styles.errorTip}>
          <InfoCircleFilled className={styles.anticon} /> 该图已在画板&nbsp;
          <a
            href={`/boards/${exist_pin.board.board_id}`}
            target={"_blank"}
            rel="noreferrer"
          >
            <span style={{ fontWeight: "bolder" }}>
              {exist_pin.board.title}
            </span>
          </a>
        </div>
      );
    }
  }, [pinExistedResult]);

  const onAvailBoardsUpdate = useMemoizedFn((boards) => {
    availBoards.current = boards;
    if (boards.length) {
      setSelected([boards[0].board_id]);
    }
  });

  const flush = useCallback(() => setUpdateAt(Date.now()), []);
  const onTagsChange = useCallback(
    (newTags) => {
      result.current.tags = newTags.slice(0, 10);
      flush();
    },
    [flush]
  );
  const onDescriptionChange = useCallback(
    (e) => {
      result.current.description = e.target.value.slice(0, 255);
      flush();
    },
    [flush]
  );
  const onKeywordChange = useCallback((e) => {
    const val = e.target.value;
    setKeyword(val.slice(0, 32));
  }, []);
  const onKeywordClear = useCallback(() => {
    setKeyword("");
  }, []);
  const onCreateBoardClick = useCallback(async () => {
    await createBoard({ title: keyword?.trim(), description: "" });
    runGetLastBoards({ extra: "cover" });
  }, [keyword, runGetLastBoards]);
  const canItemShow = useCallback((board) => !board.extra?.is_creation, []);
  const onClose = useCallback(() => {
    const { pin, board, taskId } = doneResult.current;
    taskId && clearTimeout(taskId);
    onCloseCreator && onCloseCreator(pin, board);
  }, [onCloseCreator]);
  const pinToBoard = useCallback(
    async (board) => {
      const { description, tags } = result.current;
      const data = {
        board_id: board.board_id,
        text: description ?? "",
        tags,
        via: pinId,
      };
      const { pin, err } = await createPin(data);
      if (err) {
        return;
      }

      setNewPin(pin);

      doneResult.current = {
        pin,
        board,
        onClose,
      };
      const close = (seconds) => {
        if (seconds <= 1) {
          delete doneResult.current.taskId;
          onClose();
        } else {
          --seconds;
          setAutoCloseCounter(seconds);
          doneResult.current.taskId = setTimeout(() => {
            close(seconds);
          }, 1000);
        }
      };

      if (!showSuccessTip) {
        close(0);
        return;
      }

      if (closeAfterDone) {
        doneResult.current.taskId = setTimeout(
          () => close(autoCloseCounter),
          1000
        );
      }
    },
    [autoCloseCounter, closeAfterDone, onClose, pinId, showSuccessTip]
  );

  const init = useCallback(async () => {
    if (!process.browser) return;

    try {
      if (!props.boards) {
        runGetLastBoards({ extra: "cover" });
      }
      runCheckPinExisted({ pin_id: pinId });
      const { pin } = await getPin({ pin_id: pinId });
      if (!props.targetAvatarUrl) {
        setTargetAvatarUrl(imageUrl(pin.file, { width: 400 }));
      }
      if (!props.suggestTags) {
        runGetBoardSuggestTags({ board_id: pin.board_id });
      }
      result.current.description = pin.raw_text;
    } catch (err) {
      console.error(err);
      notification.error({
        message: `出错啦`,
        description: (
          <>
            <h3>请求异常</h3>
            <p>{err.toString()}</p>
          </>
        ),
      });
    }
  }, [
    pinId,
    result,
    props.suggestTags,
    props.boards,
    props.targetAvatarUrl,
    runCheckPinExisted,
    runGetLastBoards,
    runGetBoardSuggestTags,
  ]);

  useEffect(() => {
    init();

    return () => {
      const { onClose, taskId } = doneResult.current;
      if (!taskId) return;
      clearTimeout(taskId);
      delete doneResult.current.taskId;
      onClose && onClose();
    };
    // eslint-disable-next-line
  }, []); //  only run on Mounted

  return {
    ...props,

    loading,

    boards,
    keyword,
    tip,
    targetAvatarUrl,
    suggestTags,
    result,
    newPin,
    closeAfterDone,
    autoCloseCounter,
    availBoards,
    selected,

    // methods
    onTagsChange,
    onDescriptionChange,
    onKeywordChange,
    onCreateBoardClick,
    canItemShow,
    onAvailBoardsUpdate,
    pinToBoard,
    setSelected,
    getSelected,
    onKeywordClear,
    setBoards,
  };
}
